import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { ColDef } from "ag-grid-community";
import AgGridTable from "../../../components/table/AgGridTable";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { useNavigate } from "react-router-dom";
import WhiteTooltip from "../../../components/WhiteTooltip";
import { SkeletonLoader } from "../../../modules/dashboard/shared";
import moment from "moment";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import { useDispatch } from "react-redux";
import { setConfigDiffData } from "./ConfigDiff-slice";
import ViewSummaryDrawer from "./ViewSummaryDrawer";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axiosInstance from "../../../axios/axios";
import { END_POINTS } from "../../../shared";

type AssetHistoryApiRecord = {
  scan_id?: string;
  id: string;
  timestamp?: string;
  no_of_changes?: number;
  score_changes?: number;
  misconfiguration_changes?: number;
  cve_changes?: number;
  status?: string;
  file_id?: string;
  date_added?: any;
  previous_file?: any;
  security_score?: any;
  change_breakdown?: {
    all?: number;
    additions?: number;
    deletions?: number;
    modified?: number;
  };
};

type HistoryTableRow = {
  id: string | number;
  timeStamp: string;
  numberOfChanges: {
    all: number | string;
    addition: number | string;
    deletion: number | string;
    modified: number | string;
  };
  no_of_changes: {
    all: number | string;
    addition: number | string;
    deletion: number | string;
    modified: number | string;
  };
  vendor: number | string;
  misconfiguration_changes: number | string;
  cve_changes: number | string;
  status?: string;
  isAddedToCompare?: boolean;
  raw?: AssetHistoryApiRecord | null;
};

type HistoryTableProps = {
  height?: number | string;
  device_id?: string;
  historyData?: AssetHistoryApiRecord[] | null;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number, pageSize: number) => void;
  isLoading?: boolean;
  vendor?: string | null;
  /** Narrow overlay: keep the Show Diff and compare icons from being clipped. */
  compact?: boolean;
  /** Which view triggered this table — used by ShowConfigDiffPage to decide back-navigation behaviour */
  source?: "assets-page" | "configdiff-sidebar";
};

export interface SummaryData {
  configurationChanges: {
    all_changes: number;
    additions: number;
    deletions: number;
    modified: number;
  };
  vulnerabilityChanges: {
    total_changes: number;
    added: number;
    removed: number;
  };
  cveChanges: {
    total_count: number;
    added: number;
    removed: number;
  };
}

const AssetHistoryTable: React.FC<HistoryTableProps> = ({
  height,
  device_id,
  historyData,
  page = 1,
  pageSize = 20,
  onPageChange,
  isLoading,
  vendor,
  source = "configdiff-sidebar",
  compact = false,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const gridRef = useRef<any>(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const defaultPageSize = 20;
  const [selectedRows, setSelectedRows] = useState<Array<string | number>>([]);
  const [currentPageSize, setCurrentPageSize] = useState<number>(defaultPageSize);
  const isBlockedVendor = useMemo(() => {
    const v = (vendor ?? "").toLowerCase().trim();
    if (!v) return false;
    const blockedVendors = [
      "ubuntu",
      "debian",
      "redhat",
      "microsoft",
      "huawei",
      "mikrotik",
      "edgecore",
      "tp-link",
      "tplink",
      "dell",
      "apple",
      "macos",
      "mac",
      "mac os",
      "mac os x",
    ];
    return blockedVendors.some(
      (b) => v === b || v.startsWith(b) || v.includes(b)
    );
  }, [vendor]);
  const [assetSummary, setAssetSummary] = useState<any>(null);
  const [isSummaryDrawerOpen, setIsSummaryDrawerOpen] = useState(false);
  const [summaryDataLoading, setSummaryDataLoading] = useState(false);
  // const customSortOrder = ["Critical", "High", "Medium", "Low"];

  const fetchScanChangesData = async (asset_id: string, scan_id: string) => {
    try {
      const response = await axiosInstance.get(
        END_POINTS.ASSET_VIEWSUMMARY,
        { params: { asset_id, scan_id } }
      );

      if (response.data?.status === 200) {
        const scan = response.data.data?.scans?.[0];

        if (!scan) return;

        setAssetSummary({
          configuration_changes: scan.configuration_changes,
          vulnerability_summary: scan.vulnerability_summary,
          vulnerability_changes: scan.vulnerability_changes || [],
          cve_summary: scan.cve_summary,
          cve_changes: scan.cve_changes || [],
          is_first_scan: scan.is_first_scan,
        });
      }
    } catch (error) {
      console.error("Error fetching ASSET_VIEWSUMMARY:", error);
    }
  };

  const getDeltaMeta = (
    value: number | string | undefined | null
  ) => {
    if (value === null || value === undefined) {
      return { dir: "none", color: "#667085", text: "0" };
    }

    const raw = value.toString().trim();
    const num = Number(raw.replace("+", "").replace("-", ""));

    if (isNaN(num) || num === 0) {
      return { dir: "none", color: "#667085", text: "0" };
    }

    if (raw.startsWith("+")) {
      return { dir: "up", color: "#22C55E", text: num.toString() };
    }

    if (raw.startsWith("-")) {
      return { dir: "down", color: "#F44336", text: num.toString() };
    }

    // no + or - → just show value
    return { dir: "none", color: "#667085", text: num.toString() };
  };

  const toSignedNumber = (val: any): number => {
    if (val === null || val === undefined) return 0;
    if (typeof val === "number") return val;
    if (typeof val === "string") {
      const num = Number(val.replace("+", ""));
      return isNaN(num) ? 0 : num;
    }
    return 0;
  };

  const summaryData = useMemo(() => {
    const config = assetSummary?.configuration_changes ?? {};
    const vuln = assetSummary?.vulnerability_summary ?? {};
    const cve = assetSummary?.cve_summary ?? {};

    return {
      is_first_scan: assetSummary?.is_first_scan,
      configurationChanges: {
        all_changes: toSignedNumber(config.all_changes),
        additions: toSignedNumber(config.additions),
        deletions: toSignedNumber(config.deletions),
        modified: toSignedNumber(config.modified),
      },

      vulnerabilityChanges: {
        total_changes: toSignedNumber(vuln.total_changes),
        added: toSignedNumber(vuln.added),
        removed: toSignedNumber(vuln.removed),
      },

      cveChanges: {
        total_count: toSignedNumber(cve.total_count),
        added: toSignedNumber(cve.added),
        removed: toSignedNumber(cve.removed),
      },

      misconfigurationChanges: assetSummary?.vulnerability_changes || [],
      cves: assetSummary?.cve_changes || [],
    };
  }, [assetSummary]);

  const handleOpenSummary = async (row?: HistoryTableRow) => {
    setIsSummaryDrawerOpen(true);

    if (!row || !row.raw || !device_id) {
      setAssetSummary({
        total: 0,
        additions: 0,
        deletions: 0,
        modified: 0,
      });
      return;
    }
    const asset_id = device_id || "";
    const scan_id = row.raw.id;
    // const historyId = String(row.id);

    // Derive configuration diff stats
    const changes = (row.no_of_changes || row.numberOfChanges) as any;
    const additions = parseToNumber(changes?.addition) || 0;
    const deletions = parseToNumber(changes?.deletion) || 0;
    const modified = parseToNumber(changes?.modified) || 0;
    const total =
      parseToNumber(changes?.all) || additions + deletions + modified;

    setAssetSummary({
      total: Math.abs(total),
      additions: Math.abs(additions),
      deletions: Math.abs(deletions),
      modified: Math.abs(modified),
    });
    setSummaryDataLoading(true);
    // Fetch summary data
    try {
      await Promise.all([
        fetchScanChangesData(asset_id, scan_id),
      ]);
    } catch (error) {
      console.error("Error loading summary data:", error);
    } finally {
      setSummaryDataLoading(false);
    }
  };

  const handleCloseSummary = () => {
    setIsSummaryDrawerOpen(false);
  };

  const parseToNumber = (value: number | string | undefined | null) => {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      // Handle strings with + or - prefix like "+38", "-37", "+1"
      const cleanedValue = value.replace(/^\+/, ''); // Remove leading + if present
      const num = Number(cleanedValue);
      return isNaN(num) ? null : num;
    }
    return null;
  };

  const renderDelta = (
    value: number | string | undefined | null,
    opts?: { isBaseline?: boolean; invertSignColors?: boolean; invertArrows?: boolean }
  ) => {
    const isBaseline = opts?.isBaseline;
    const baselineText = "0";

    if (isBaseline) {
      return (
        <span className="ag-ellipsis" style={{ color: "#667085", cursor: "pointer" }}>
          {baselineText}
        </span>
      );
    }

    const meta = getDeltaMeta(value);

    // Optionally invert the colors for +/− semantics (used by CVE column)
    let displayColor = meta.color;
    if (opts?.invertSignColors) {
      if (meta.dir === "up") displayColor = "#F44336"; // + → red
      if (meta.dir === "down") displayColor = "#22C55E"; // - → green
    }

    // Determine which icon to show based on direction and optional inversion
    let ShowUpIcon = meta.dir === "up";
    let ShowDownIcon = meta.dir === "down";

    if (opts?.invertArrows) {
      // Invert the logic: Up direction shows Down icon, Down direction shows Up icon
      if (meta.dir === "up") {
        ShowUpIcon = false;
        ShowDownIcon = true;
      } else if (meta.dir === "down") {
        ShowUpIcon = true;
        ShowDownIcon = false;
      }
    }

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: displayColor, cursor: "pointer" }}>
        {ShowUpIcon && <ArrowOutwardIcon fontSize="inherit" sx={{ fontSize: 14, color: displayColor }} />}
        {ShowDownIcon && <CallReceivedIcon fontSize="inherit" sx={{ fontSize: 14, color: displayColor }} />}
        <span style={{ color: displayColor, cursor: "pointer" }}>{meta.text}</span>
      </Box>
    );
  };

  const normalizedHistoryData: HistoryTableRow[] = useMemo(() => {
    if (!Array.isArray(historyData) || historyData.length === 0) {
      return [];
    }

    return historyData.map((item, idx) => {
      const breakdown = item.change_breakdown ?? {};

      const ensureValue = (value?: number | string | null) => {
        const parsed = parseToNumber(value as any);
        return parsed === null ? 0 : parsed;
      };

      // Use ensureValue for all values since API returns strings like "+21", "-9"
      const resolvedAll = breakdown.all !== undefined
        ? ensureValue(breakdown.all)
        : (typeof item.no_of_changes === "number" ? item.no_of_changes : 0);

      return {
        id: item.scan_id ?? `scan_${idx}`,
        timeStamp: item.timestamp ?? "—",
        numberOfChanges: {
          all: Math.round(Math.abs(resolvedAll)),
          addition: Math.round(Math.abs(ensureValue(breakdown.additions))),
          deletion: Math.round(Math.abs(ensureValue(breakdown.deletions))),
          modified: Math.round(Math.abs(ensureValue(breakdown.modified))),
        },
        no_of_changes: {
          all: breakdown.all !== undefined ? breakdown.all.toString() : (item.no_of_changes?.toString() ?? "0"),
          addition: breakdown.additions !== undefined ? breakdown.additions.toString() : "0",
          deletion: breakdown.deletions !== undefined ? breakdown.deletions.toString() : "0",
          modified: breakdown.modified !== undefined ? breakdown.modified.toString() : "0",
        },
        vendor:
          typeof item.score_changes === "number" ? Math.round(item.score_changes) : 0,
        misconfiguration_changes: (() => {
          if (item.misconfiguration_changes == null) return "0";

          const str = item.misconfiguration_changes.toString().trim();
          const num = Math.round(Math.abs(Number(str.replace("+", "").replace("-", ""))));

          if (str.startsWith("-")) return `-${num}`;
          if (str.startsWith("+")) return `+${num}`;

          return num.toString(); // no sign → plain value
        })(),
        cve_changes: (() => {
          if (item.cve_changes === undefined || item.cve_changes === null) return "0";
          const str = item.cve_changes.toString();
          const hasPlus = str.startsWith('+');
          const hasMinus = str.startsWith('-');
          const num = Math.round(Math.abs(parseFloat(str.replace('+', ''))));
          if (hasMinus) return `-${num}`;
          if (hasPlus || num > 0) return `+${num}`;
          return "0";
        })(),
        status: item.status,
        isAddedToCompare: false,
        raw: item,
      };
    });
  }, [historyData]);

  useEffect(() => {
    if (!normalizedHistoryData.length) {
      setSelectedRows([]);
      return;
    }

    setSelectedRows((prev) => {
      const currentIds = new Set(normalizedHistoryData.map((row) => row.id));
      const stillValid = prev.filter((id) => currentIds.has(id));
      // Keep only currently valid selections and avoid auto-selecting rows by default
      return stillValid.slice(0, 2);
    });
  }, [normalizedHistoryData]);

  // Ensure page size is set to 20 when grid is ready
  useEffect(() => {
    if (gridRef?.current?.api && normalizedHistoryData.length > 0) {
      const apiPageSize = gridRef.current.api.paginationGetPageSize();
      if (apiPageSize !== currentPageSize) {
        gridRef.current.api.paginationSetPageSize(currentPageSize);
      }
    }
  }, [normalizedHistoryData, currentPageSize]);

  const handleAddToCompare = (rowId: string | number) => {
    setSelectedRows((prev) => {
      if (prev.includes(rowId)) {
        // Remove if already selected
        return prev.filter((id) => id !== rowId);
      } else {
        // Add only if less than 2 are selected
        if (prev.length < 2) {
          return [...prev, rowId];
        }
        return prev;
      }
    });
  };

  const handleShowDiff = (rowId: string | number) => {
    const rowData = normalizedHistoryData.find((item) => item.id === rowId);
    if (rowData) {
      // Dispatch row data to Redux store
      dispatch(setConfigDiffData({
        rowData: {
          ...(rowData.raw ?? rowData),
          device_id: device_id || (rowData as any).device_id,
        } as any,
        selectedRows: null,
        device_id: device_id || null,
        click: "asset-config-diff",
        source,
      }));
      // Navigate to ShowConfigDiffPage
      navigate("/on-premises/configdiff/show-diff");
    }
  };

  useEffect(() => {
    setSelectedRows([]);
  }, [device_id, historyData]);

  const handleCompareClick = () => {
    if (selectedRows.length !== 2) {
      return;
    }

    const selectedRowData = selectedRows
      .map((rowId) => normalizedHistoryData.find((item) => item.id === rowId))
      .filter((row): row is HistoryTableRow => Boolean(row));

    if (selectedRowData.length !== 2) {
      return;
    }

    const selectedRowsPayload = selectedRowData.map((row) => ({
      ...(row.raw ?? row),
      device_id: device_id || (row as any).device_id,
    }));

    // Dispatch comparison data to Redux store
    dispatch(setConfigDiffData({
      rowData: null,
      selectedRows: selectedRowsPayload as any,
      device_id: device_id || null,
      click: "asset-config-diff",
      source,
    }));

    // Navigate to ShowConfigDiffPage
    navigate("/on-premises/configdiff/show-diff");
  };

  const columnDefs: ColDef[] = useMemo(
    () => {
      const baseColumns: ColDef[] = [
        {
          field: "timeStamp",
          headerName: "Time Stamp",
          flex: compact ? 1.4 : 1,
          minWidth: compact ? 128 : 175,
          filter: false,
          sortable: true,
          cellRenderer: (params: any) => {
            const formattedTime = params.value
              ? moment(params.value).format("DD-MMM-YYYY HH:mm:ss")
              : "-";

            return (
              <WhiteTooltip title={formattedTime} arrow>
                <span className="ag-ellipsis" style={{ color: "#667085", cursor: "pointer" }}>
                  {formattedTime}
                </span>
              </WhiteTooltip>
            );
          },
        },
        {
          field: "no_of_changes",
          headerName: "No. of Changes",
          minWidth: compact ? 96 : 160,
          flex: compact ? 0.9 : 1,
          filter: false,
          sortable: true,
          cellRenderer: (params: any) => {
            const isBaseline = params?.rowIndex === 0;
            const changes = params.value;
            if (!changes || typeof changes !== "object") {
              return (
                <span className="ag-ellipsis" style={{ color: "#667085", cursor: "pointer" }}>
                  0
                </span>
              );
            }
            const tooltipItems = [
              { label: "All", value: isBaseline ? 0 : changes.all, type: 'all' as const },
              { label: "Addition", value: isBaseline ? 0 : changes.addition, type: 'addition' as const },
              { label: "Deletion", value: isBaseline ? 0 : changes.deletion, type: 'deletion' as const },
              { label: "Modified", value: isBaseline ? 0 : changes.modified, type: 'modified' as const },
            ];
            return (
              <WhiteTooltip
                title={
                  <Box
                    sx={{
                      p: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                    }}
                  >
                    {tooltipItems.map((item) => {
                      const meta = isBaseline
                        ? { dir: "none" as const, color: "#667085", text: "0" }
                        : getDeltaMeta(item.value);
                      return (
                        <Box
                          key={item.label}
                          sx={{ display: "flex", alignItems: "center", gap: 0.75, color: isDarkMode ? "#000000" : "#000000" }}
                        >
                          <span className="ag-ellipsis">{item.label}:</span>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, color: meta.color }}>
                            {meta.dir === "up" && (
                              <ArrowOutwardIcon sx={{ fontSize: 12 }} />
                            )}
                            {meta.dir === "down" && (
                              <CallReceivedIcon sx={{ fontSize: 12 }} />
                            )}
                            <span>{meta.text}</span>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                }
                arrow
                slotProps={{
                  tooltip: {
                    sx: {
                      bgcolor: "common.white",
                      color: "text.primary",
                      border: "1px solid",
                      borderColor: "divider",
                      boxShadow: 3,
                    },
                  },
                  arrow: {
                    sx: { color: "common.white" },
                  },
                }}
              >
                <span className="ag-ellipsis" style={{ color: "#667085", cursor: "pointer" }}>
                  {isBaseline ? 0 : Math.abs(Math.round(changes.all))}
                </span>
              </WhiteTooltip>
            );
          },
        },
        // {
        //   field: "vendor",
        //   headerName: "Score Changes ",
        //   minWidth: 140,
        //   flex: 1,
        //   filter: false,
        //   sortable: true,
        //   cellRenderer: (params: any) =>
        //     renderDelta(params.value, { isBaseline: params?.rowIndex === 0 }),
        // },
        {
          field: "misconfiguration_changes",
          headerName: "Misconfiguration",
          minWidth: compact ? 120 : 200,
          flex: 1,
          filter: false,
          sortable: true,
          cellRenderer: (params: any) =>
            renderDelta(params.value, { isBaseline: params?.rowIndex === 0, invertArrows: true }),
        },
        {
          field: "cve_changes",
          headerName: "CVE",
          minWidth: compact ? 64 : 140,
          flex: compact ? 0.5 : 1,
          filter: false,
          sortable: true,
          // Invert sign colors: show red for '+' and green for '-'
          cellRenderer: (params: any) =>
            renderDelta(params.value, { isBaseline: params?.rowIndex === 0, invertSignColors: true }),
        },

        // {
        //   field: 'approvalDate',
        //   headerName: 'Approval Date',
        //   width: 140,
        //   filter: false,
        //   sortable: true,
        //   cellRenderer: (params: any) => (
        //     <Tooltip title={params.value} arrow>
        //       <span className="ag-ellipsis">{params.value || '-'}</span>
        //     </Tooltip>
        //   ),
        // },
        // {
        //   field: 'approvalBy',
        //   headerName: 'Approval by',
        //   width: 130,
        //   filter: false,
        //   sortable: true,
        //   cellRenderer: (params: any) => (
        //     <Tooltip title={params.value} arrow>
        //       <span className="ag-ellipsis">{params.value || '-'}</span>
        //     </Tooltip>
        //   ),
        // },
      ];

      if (!isBlockedVendor) {
        baseColumns.push(
          {
            field: "showDiff",
            headerName: compact ? "Diff" : "Show Diff",
            headerTooltip: "Show Diff",
            width: compact ? 56 : 120,
            minWidth: compact ? 56 : 120,
            maxWidth: compact ? 64 : undefined,
            flex: compact ? 0 : undefined,
            filter: false,
            sortable: false,
            cellRenderer: (params: any) => (
              <div
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                style={{ display: 'flex', alignItems: 'center', height: '100%' }}
              >
                <IconButton
                  size="small"
                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleShowDiff(params.data.id);
                  }}
                  sx={{
                    color: theme.palette.primary.main,
                    "&:hover": {
                      backgroundColor: "rgba(25, 118, 210, 0.08)",
                    },
                  }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </div>
            ),
          },
          {
            field: "addToCompare",
            headerName: compact ? "Add" : "Add to Compare",
            headerTooltip: "Add to Compare",
            width: compact ? 56 : 150,
            minWidth: compact ? 56 : 150,
            maxWidth: compact ? 64 : undefined,
            flex: compact ? 0 : undefined,
            filter: false,
            sortable: false,
            cellRenderer: (params: any) => {
              const isSelected = selectedRows.includes(params.data.id);
              const isCompareAllowed = normalizedHistoryData.length > 1;
              const isDisabled =
                !isCompareAllowed ||
                (selectedRows.length === 2 && !isSelected);
              return (
                <div
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  style={{ display: 'flex', alignItems: 'center', height: '100%' }}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddToCompare(params.data.id);
                    }}
                    disabled={isDisabled}
                    sx={{
                      color: isSelected
                        ? theme.palette.primary.main
                        : theme.palette.primary.main,
                      "&:hover": {
                        backgroundColor: isDisabled
                          ? "transparent"
                          : "rgba(25, 118, 210, 0.08)",
                      },
                      "&.Mui-disabled": {
                        color: theme.palette.action.disabled,
                      },
                    }}
                  >
                    {isSelected ? (
                      <RemoveCircleIcon fontSize="small" />
                    ) : (
                      <AddCircleOutlineIcon fontSize="small" />
                    )}
                  </IconButton>
                </div>
              );
            },
          }
        );
      }

      return baseColumns;
    },
    [selectedRows, theme, isBlockedVendor, compact]
  );

  // const computedMinHeight =
  //   typeof height === "number" ? Math.min(height, 200) : 200;
  const isCompareDisabled = selectedRows.length !== 2;
  const shouldHideVerticalScroll = normalizedHistoryData.length <= pageSize;
  const agGridStyles = useMemo(() => {
    const baseStyles: any = {
      "& .ag-row": {
        cursor: "pointer !important",
      },
      "& .ag-cell": {
        cursor: "pointer !important",
      },
      "& .ag-cell-value": {
        cursor: "pointer !important",
      },
      "& .ag-cell-wrapper": {
        cursor: "pointer !important",
      },
      "& .ag-ellipsis": {
        cursor: "pointer !important",
      },
      "& span": {
        cursor: "pointer !important",
      },
      // Keep cursor default for action columns, but pointer on icons
      "& .ag-cell[col-id='showDiff']": {
        cursor: "pointer !important",
        "& *": {
          cursor: "pointer !important",
        },
        "& .MuiIconButton-root": {
          cursor: "pointer !important",
        },
        "& .MuiIconButton-root *": {
          cursor: "pointer !important",
        },
      },
      "& .ag-cell[col-id='addToCompare']": {
        cursor: "pointer !important",
        "& *": {
          cursor: "pointer !important",
        },
        "& .MuiIconButton-root": {
          cursor: "pointer !important",
        },
        "& .MuiIconButton-root *": {
          cursor: "pointer !important",
        },
      },
    };

    if (shouldHideVerticalScroll) {
      baseStyles["& .ag-center-cols-clipper"] = {
        marginRight: 0,
      };
      baseStyles["& .ag-center-cols-viewport"] = {
        marginRight: 0,
      };
    }

    if (compact) {
      baseStyles["& .ag-body-horizontal-scroll"] = {
        display: "none",
        height: "0px",
        minHeight: "0px",
        maxHeight: "0px",
      };
      baseStyles["& .ag-horizontal-left-spacer"] = { display: "none" };
      baseStyles["& .ag-horizontal-right-spacer"] = { display: "none" };
    }

    return baseStyles;
  }, [shouldHideVerticalScroll, compact]);

  const hasNoData = !isLoading && normalizedHistoryData.length === 0;

  return (
    <Accordion
      expanded={isExpanded}
      onChange={(_e, expanded) => setIsExpanded(expanded)}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "8px",
        bgcolor: theme.palette.mode === "dark" ? "#1E1E1E" : "#ffffff",
        boxShadow: 'none',
        '&:before': { display: 'none' },
        mb: 0,
      }}
      disableGutters
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon
            sx={{
              color: theme.palette.text.primary,
              transition: 'transform 0.3s',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        }
        sx={{
          flexDirection: 'row-reverse',
          borderBottom: isExpanded ? `1px solid ${theme.palette.divider}` : 'none',
          minHeight: '48px',
          p: 0,
          '& .MuiAccordionSummary-content': {
            margin: 0,
            alignItems: 'center',
            width: '100%',
            paddingLeft: 8,
            paddingRight: 8,
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: '100%' }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              fontSize: "16px",
              color: theme.palette.text.primary,
            }}
          >
            History
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <InfoIcon
                fontSize="small"
                sx={{ color: theme.palette.text.secondary }}
              />
              <Typography
                variant="body2"
                sx={{ fontSize: "12px", fontWeight: 600 }}
              >
                {selectedRows.length} out of 2 Added in Compare
                Selections
              </Typography>
            </Box>
            {!isBlockedVendor && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                disabled={isCompareDisabled}
                onClick={handleCompareClick}
                sx={{
                  fontWeight: 700,
                  color: isCompareDisabled
                    ? "#9DA4AE"
                    : isDarkMode
                      ? "#FFFFFF"
                      : "#3A2ADA",
                  borderColor: "#3A2ADA",
                  "&.Mui-disabled": {
                    borderColor: "#E0E3EB",
                  },
                }}
              >
                Compare
              </Button>

            )}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <Box
          sx={{
            height: height ?? "220px",
            minHeight: 250,
            maxHeight: 270,
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* 1️⃣ Loading state – ONLY skeleton */}
          {isLoading && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                width: "100%",
              }}
            >
              <SkeletonLoader count={13} styles={{ height: 40, width: "100%" }} />
            </Box>
          )}

          {/* 2️⃣ No data state */}
          {!isLoading && hasNoData && (
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}
            >
              No data available
            </Typography>
          )}

          {/* 3️⃣ Data available – render AgGrid */}
          {!isLoading && !hasNoData && (
            <AgGridTable
              columnDefs={columnDefs}
              data={normalizedHistoryData}
              height="100%"
              pagination
              paginationPageSize={currentPageSize}
              showAppsIcon={false}
              styles={agGridStyles}
              gridRef={gridRef}
              onRowClick={(rowData: HistoryTableRow) => {
                // Open summary drawer when row is clicked
                handleOpenSummary(rowData);
              }}
              gridOptions={{
                paginationPageSize: currentPageSize,

                onGridReady: (event: any) => {
                  if (event.api?.paginationSetPageSize) {
                    event.api.paginationSetPageSize(currentPageSize);
                  }
                  if (compact && event.api?.sizeColumnsToFit) {
                    event.api.sizeColumnsToFit();
                  }
                },

                onPaginationChanged: (event: any) => {
                  const api = event.api;
                  const newPage = api.paginationGetCurrentPage() + 1;
                  const newPageSize = api.paginationGetPageSize();

                  if (newPageSize !== currentPageSize) {
                    setCurrentPageSize(newPageSize);
                  }

                  if (
                    onPageChange &&
                    (newPage !== page || newPageSize !== currentPageSize)
                  ) {
                    onPageChange(newPage, newPageSize);
                  }
                },
              }}
              isLoading={false}
            />
          )}
        </Box>
      </AccordionDetails>
      <ViewSummaryDrawer
        open={isSummaryDrawerOpen}
        onClose={handleCloseSummary}
        summaryData={summaryData}
        isLoading={summaryDataLoading}
        vendor={vendor}
      />
    </Accordion>
  );
};

export default AssetHistoryTable;
